import type { Theme } from "@mui/material";

import type { TagType, TagValue } from "src/types/tag";

type TagColorSource = Pick<TagType, "color" | "name" | "slug">;
type TagValueColorSource = Pick<TagValue, "color">;

export type ResolvedTagColors = {
    background: string;
    foreground: string;
};

const COUNTRY_TAG_KEYS = new Set([
    "country",
    "country-of-production",
    "pays",
    "pays-de-production",
    "production-country",
]);

const LABEL_TAG_KEYS = new Set(["donnee-specifique", "donnees-specifiques", "label", "labels", "specific-data"]);

const normalizeTagKey = (value: string) =>
    value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

const isColorBright = (color: string): boolean => {
    if (!color) return false;

    const hex = color.replace("#", "");
    const red = Number.parseInt(hex.slice(0, 2), 16);
    const green = Number.parseInt(hex.slice(2, 4), 16);
    const blue = Number.parseInt(hex.slice(4, 6), 16);
    const brightness = (red * 299 + green * 587 + blue * 114) / 1000;

    return brightness > 155;
};

/** Resolves the API-provided tag colors used by the legacy presentation. */
export const resolveLegacyTagColors = (tagType?: TagColorSource, tagValue?: TagValueColorSource): ResolvedTagColors => {
    const background =
        tagValue?.color !== undefined && tagValue.color !== "" ? tagValue.color : tagType?.color || "#ccc";

    return {
        background,
        foreground: isColorBright(background) ? "black" : "white",
    };
};

/** Resolves the semantic colors defined by Product V2 before tenant API fallbacks. */
export const resolveTagColors = (
    theme: Theme,
    tagType: TagColorSource,
    tagValue?: TagValueColorSource,
): ResolvedTagColors => {
    const keys = [normalizeTagKey(tagType.slug), normalizeTagKey(tagType.name)];

    if (keys.some((key) => COUNTRY_TAG_KEYS.has(key))) {
        return {
            background: theme.custom.dataviz.extra[13].main,
            foreground: theme.custom.grey[100],
        };
    }

    if (keys.some((key) => LABEL_TAG_KEYS.has(key))) {
        return {
            background: theme.custom.dataviz.extra[11].main,
            foreground: theme.custom.grey[100],
        };
    }

    const background = tagValue?.color?.trim() || tagType.color.trim() || theme.custom.grey[600];

    return {
        background,
        foreground: theme.palette.getContrastText(background),
    };
};
